import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { BranchOperationalRow } from "../types/dashboard.type";
import { ALL_TIME_MONTH_VALUE } from "./dashboardMonth.helpers";
import { fetchImageAsDataUrl } from "./reportImage.helpers";

const ZERO_PLATFORM_LOGO_PATH = "/images/zero_light.png";
const REPORT_RGB = { r: 0, g: 82, b: 204 } as const;
const PAGE_MARGIN_MM = 16;
const CONTENT_START_MM = 52;
const HEADER_BOTTOM_LINE_Y_MM = 42;
const BAR_COLORS: [number, number, number][] = [
  [124, 58, 237],
  [176, 122, 30],
  [61, 90, 128],
  [168, 68, 42],
  [140, 109, 90],
  [51, 65, 85],
];

const UNASSIGNED_SITE_ID = "__unassigned__";

function safeNum(value: number | null | undefined): number {
  if (value == null || Number.isNaN(Number(value))) return 0;
  return Number(value);
}

function fmtNum(n: number, decimals = 2): string {
  return safeNum(n).toLocaleString(undefined, { maximumFractionDigits: decimals });
}

function imageFormat(dataUrl: string): "PNG" | "JPEG" {
  return dataUrl.startsWith("data:image/jpeg") ? "JPEG" : "PNG";
}

function drawContainedLogo(
  doc: jsPDF,
  dataUrl: string,
  slotX: number,
  slotY: number,
  slotW: number,
  slotH: number,
) {
  const props = doc.getImageProperties(dataUrl);
  const ratio = props.width / props.height;
  let drawH = slotH;
  let drawW = drawH * ratio;
  if (drawW > slotW) {
    drawW = slotW;
    drawH = drawW / ratio;
  }
  const drawY = slotY + (slotH - drawH) / 2;
  doc.addImage(dataUrl, imageFormat(dataUrl), slotX, drawY, drawW, drawH);
}

function drawReportHeader(
  doc: jsPDF,
  pageWidth: number,
  margin: number,
  platformLogoDataUrl: string | null,
  orgLogoDataUrl: string | null,
  organizationName: string,
  periodLabel: string,
  generatedLabel: string,
) {
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.5);
  doc.line(0, HEADER_BOTTOM_LINE_Y_MM, pageWidth, HEADER_BOTTOM_LINE_Y_MM);

  const logoY = 14;
  const slotH = 16;
  const slotW = 30;
  if (platformLogoDataUrl) {
    drawContainedLogo(doc, platformLogoDataUrl, margin, logoY, slotW, slotH);
  }
  if (orgLogoDataUrl) {
    drawContainedLogo(doc, orgLogoDataUrl, margin + slotW + 4, logoY, slotW, slotH);
  }

  const metaX = pageWidth - margin;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(51, 65, 85);
  doc.text(organizationName, metaX, 16, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(periodLabel, metaX, 22, { align: "right" });
  doc.text(generatedLabel, metaX, 28, { align: "right" });
}

function ensureVerticalSpace(
  doc: jsPDF,
  y: number,
  threshold: number,
  drawHeader: () => void,
): number {
  if (y <= threshold) return y;
  doc.addPage();
  drawHeader();
  return CONTENT_START_MM;
}

function drawPageFooters(doc: jsPDF, pageWidth: number, margin: number, footerText: string) {
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageH = doc.internal.pageSize.getHeight();
    doc.setFillColor(REPORT_RGB.r, REPORT_RGB.g, REPORT_RGB.b);
    doc.rect(0, pageH - 12, pageWidth, 12, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(footerText, margin, pageH - 4);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageH - 4, { align: "right" });
  }
}

function truncateText(text: string, max = 32): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function drawMetricBars(
  doc: jsPDF,
  y: number,
  margin: number,
  contentWidth: number,
  title: string,
  entries: { label: string; value: number }[],
  unit: string,
  maxValue: number,
): number {
  const rowHeight = 7;
  const labelWidth = 48;
  const barMaxWidth = contentWidth - labelWidth - 28;

  doc.setTextColor(REPORT_RGB.r, REPORT_RGB.g, REPORT_RGB.b);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text(title, margin, y);
  y += 7;

  entries.forEach((entry, index) => {
    const barW = maxValue > 0 ? (entry.value / maxValue) * barMaxWidth : 0;
    const color = BAR_COLORS[index % BAR_COLORS.length];

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);
    doc.text(truncateText(entry.label, 24), margin, y + 4.5);

    const barX = margin + labelWidth;
    doc.setFillColor(235, 238, 242);
    doc.rect(barX, y, barMaxWidth, rowHeight - 1, "F");
    doc.setFillColor(color[0], color[1], color[2]);
    if (barW > 0) {
      doc.rect(barX, y, barW, rowHeight - 1, "F");
    }

    const valueText =
      unit === "%"
        ? `${fmtNum(entry.value, 1)}%`
        : unit
          ? `${fmtNum(entry.value)} ${unit}`
          : fmtNum(entry.value, 0);
    doc.text(valueText, barX + barMaxWidth + 2, y + 4.5);

    y += rowHeight + 2;
  });

  return y + 6;
}

export interface BranchComparisonPdfLabels {
  reportTitle: string;
  periodPrefix: string;
  allTimeLabel: string;
  generatedPrefix: string;
  branchesCompared: string;
  branchName: string;
  siteId: string;
  detailedComparison: string;
  visualComparison: string;
  metricColumn: string;
  wasteChartTitle: string;
  fillReportsChartTitle: string;
  diversionChartTitle: string;
  totalWaste: string;
  completedWaste: string;
  fillReports: string;
  diversionRate: string;
  footerText: string;
}

export interface BuildBranchComparisonPdfParams {
  organizationName: string;
  organizationLogoUrl: string | null;
  selectedMonth: string;
  branches: BranchOperationalRow[];
  branchNames: string[];
  labels: BranchComparisonPdfLabels;
}

export async function buildBranchComparisonPdf({
  organizationName,
  organizationLogoUrl,
  selectedMonth,
  branches,
  branchNames,
  labels,
}: BuildBranchComparisonPdfParams): Promise<{ doc: jsPDF; filename: string }> {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = PAGE_MARGIN_MM;
  const contentWidth = pageWidth - margin * 2;
  const rgb = REPORT_RGB;

  const platformLogoUrl = new URL(ZERO_PLATFORM_LOGO_PATH, window.location.origin).href;
  const [platformLogoDataUrl, orgLogoDataUrl] = await Promise.all([
    fetchImageAsDataUrl(platformLogoUrl),
    organizationLogoUrl?.trim()
      ? fetchImageAsDataUrl(organizationLogoUrl.trim())
      : Promise.resolve(null),
  ]);

  const orgName = organizationName || "Organization";
  const isAllTime = selectedMonth === ALL_TIME_MONTH_VALUE;
  const periodLabel = isAllTime
    ? `${labels.periodPrefix}: ${labels.allTimeLabel}`
    : `${labels.periodPrefix}: ${format(new Date(selectedMonth), "MMMM yyyy")}`;
  const generatedLabel = `${labels.generatedPrefix} ${format(new Date(), "dd MMM yyyy, HH:mm")}`;

  const drawContinuationHeader = () =>
    drawReportHeader(
      doc,
      pageWidth,
      margin,
      platformLogoDataUrl,
      orgLogoDataUrl,
      orgName,
      periodLabel,
      generatedLabel,
    );

  drawContinuationHeader();
  let y = CONTENT_START_MM;

  doc.setTextColor(rgb.r, rgb.g, rgb.b);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(labels.reportTitle, margin, y);
  y += 10;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80, 80, 80);
  doc.text(`${labels.branchesCompared}: ${branches.length}`, margin, y);
  y += 8;

  const branchListBody = branches.map((branch, index) => [
    branchNames[index] ?? "—",
    branch.collection_site_id === UNASSIGNED_SITE_ID ? "—" : branch.collection_site_id,
  ]);

  autoTable(doc, {
    startY: y,
    head: [[labels.branchName, labels.siteId]],
    body: branchListBody,
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [rgb.r, rgb.g, rgb.b], fontSize: 9, fontStyle: "bold" },
    bodyStyles: { fontSize: 8.5 },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    styles: { cellPadding: 3 },
  });

  y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
  y = ensureVerticalSpace(doc, y, 200, drawContinuationHeader);

  doc.setTextColor(rgb.r, rgb.g, rgb.b);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(labels.detailedComparison, margin, y);
  y += 2;

  const metricRows = [
    {
      label: labels.totalWaste,
      values: branches.map((b) => `${fmtNum(b.total_waste_kg)} kg`),
      numeric: branches.map((b) => safeNum(b.total_waste_kg)),
      unit: "kg",
      title: labels.wasteChartTitle,
    },
    {
      label: labels.fillReports,
      values: branches.map((b) => safeNum(b.fill_level_report_count).toLocaleString()),
      numeric: branches.map((b) => safeNum(b.fill_level_report_count)),
      unit: "",
      title: labels.fillReportsChartTitle,
    },
    {
      label: labels.diversionRate,
      values: branches.map((b) => `${fmtNum(b.waste_diversion_rate_percent, 1)}%`),
      numeric: branches.map((b) => safeNum(b.waste_diversion_rate_percent)),
      unit: "%",
      title: labels.diversionChartTitle,
    },
  ];

  const tableHead = [labels.metricColumn, ...branchNames.map((name) => truncateText(name, 22))];
  const tableBody = metricRows.map((row) => [row.label, ...row.values]);

  autoTable(doc, {
    startY: y,
    head: [tableHead],
    body: tableBody,
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [rgb.r, rgb.g, rgb.b], fontSize: 8, fontStyle: "bold" },
    bodyStyles: { fontSize: 8 },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    styles: { cellPadding: 2.5, overflow: "linebreak" },
  });

  y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 12;
  y = ensureVerticalSpace(doc, y, 180, drawContinuationHeader);

  doc.setTextColor(rgb.r, rgb.g, rgb.b);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(labels.visualComparison, margin, y);
  y += 8;

  const wasteDivertedEntries = branches.map((b, i) => ({
    label: branchNames[i] ?? "",
    value: safeNum(b.total_waste_kg),
  }));
  const fillEntries = branches.map((b, i) => ({
    label: branchNames[i] ?? "",
    value: safeNum(b.fill_level_report_count),
  }));
  const diversionEntries = branches.map((b, i) => ({
    label: branchNames[i] ?? "",
    value: safeNum(b.waste_diversion_rate_percent),
  }));

  const maxWaste = Math.max(
    ...wasteDivertedEntries.map((e) => e.value),
    0,
  );

  y = drawMetricBars(
    doc,
    y,
    margin,
    contentWidth,
    labels.totalWaste,
    wasteDivertedEntries,
    "kg",
    maxWaste,
  );
  y = ensureVerticalSpace(doc, y, 200, drawContinuationHeader);


  y = drawMetricBars(
    doc,
    y,
    margin,
    contentWidth,
    labels.fillReports,
    fillEntries,
    "",
    Math.max(...fillEntries.map((e) => e.value), 0),
  );
  y = ensureVerticalSpace(doc, y, 200, drawContinuationHeader);

  drawMetricBars(
    doc,
    y,
    margin,
    contentWidth,
    labels.diversionRate,
    diversionEntries,
    "%",
    100,
  );

  drawPageFooters(doc, pageWidth, margin, labels.footerText);

  const filename = isAllTime
    ? `RECYCLEE_Branch_Comparison_AllTime_${format(new Date(), "yyyy-MM-dd")}.pdf`
    : `RECYCLEE_Branch_Comparison_${format(new Date(selectedMonth), "yyyy-MM")}.pdf`;
  return { doc, filename };
}
